import dayjs from "dayjs";
import { jsPDF } from "jspdf";

import {
  fetchClientAddresses,
  fetchClientFiscalAddress,
} from "~/services/addressService";
import { Address, Client } from "~/types/client";
import { formatCurrency, formatRut } from "~/utils/helpers";

const LOGO_URL =
  "https://mdrvhekhimhcwydrpueo.supabase.co/storage/v1/object/public/uploads/assets/aliace-logo.jpg";

const DASH = "—";

/**
 * The mapped `Client` returned by `useClientDetail` carries a few extra runtime
 * fields (set via `as any`) that aren't on the shared `Client` interface. Surface
 * them here so the ficha can read them without widening the shared type.
 */
type ClientWithContactExtras = Client & {
  collection_contact_person?: string;
  collection_contact_email?: string;
  collection_contact_phone?: string;
  sales_channel_name?: string;
  auxiliary_type_name?: string;
};

interface ExportClientFichaOptions {
  client: Client;
  /** Resolved sales-condition name (the client only stores the id). */
  salesConditionName?: string;
}

// Load image and convert to base64 (mirrors quotationPdf / purchaseOrderPdf).
const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });
};

/** One-line "Calle N°, complemento, comuna, ciudad, región" address string. */
const formatAddressLine = (address: Address | null | undefined): string => {
  if (!address) return "";
  return [
    [address.street, address.number].filter(Boolean).join(" ").trim(),
    address.complement,
    address.comuna,
    address.city,
    address.state,
  ]
    .filter((part) => part && String(part).trim())
    .join(", ");
};

/**
 * Generates a one-page (paginated when needed) client "ficha" PDF styled like
 * the cotización: Aliace logo, centered company header, a boxed title, bordered
 * sections (información general, condiciones comerciales, contactos, dirección
 * fiscal) and a despacho-addresses table. Returns the generated filename.
 */
export async function generateClientFichaPdf({
  client,
  salesConditionName,
}: ExportClientFichaOptions): Promise<string> {
  const c = client as ClientWithContactExtras;
  const clientId = String(client.id);

  const [addresses, fiscalAddress] = await Promise.all([
    fetchClientAddresses(clientId),
    fetchClientFiscalAddress(clientId),
  ]);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const rightEdge = pageWidth - margin;

  // Try to load logo
  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImage(LOGO_URL);
  } catch (e) {
    console.warn("Could not load logo:", e);
  }

  // Trim a value so it never overflows its column; appends an ellipsis.
  const fitText = (value: unknown, maxWidth: number): string => {
    const str =
      value === null || value === undefined || value === "" ? DASH : String(value);
    if (doc.getTextWidth(str) <= maxWidth) return str;
    let truncated = str;
    while (truncated.length > 1 && doc.getTextWidth(`${truncated}…`) > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return `${truncated.trim()}…`;
  };

  // Bold label + normal value on one line, value truncated to its column.
  const field = (
    labelX: number,
    valueX: number,
    y: number,
    label: string,
    value: unknown,
    valueRight: number = rightEdge - 2
  ) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(label, labelX, y);
    doc.setFont("helvetica", "normal");
    doc.text(fitText(value, valueRight - valueX), valueX, y);
  };

  // Grey title band (matches the cotización's table-header look).
  const sectionHeader = (y: number, title: string): number => {
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y, pageWidth - 2 * margin, 6, "F");
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, y, pageWidth - 2 * margin, 6);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(title, margin + 2, y + 4);
    return y + 6;
  };

  // ==================== HEADER SECTION ====================
  let y = 12;

  if (logoBase64) {
    doc.addImage(logoBase64, "JPEG", margin, y, 40, 20);
  }

  const centerX = pageWidth / 2;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("ALIMENTOS Y ACEITES SPA", centerX, y + 4, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text(
    "COMERCIALIZACION Y DISTRIBUCION DE PRODUCTOS ALIMENTICIOS",
    centerX,
    y + 9,
    { align: "center" }
  );
  doc.text("CARRETERA GENERAL SAN MARTIN N° 9340, BOD. 6", centerX, y + 13, {
    align: "center",
  });
  doc.text("QUILICURA, SANTIAGO", centerX, y + 17, { align: "center" });
  doc.text("TELEFONOS: (+56 2) 2681 6656", centerX, y + 21, {
    align: "center",
  });

  // Title box on the right
  const boxX = pageWidth - margin - 45;
  const boxY = y;
  const boxWidth = 45;
  const boxHeight = 22;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(boxX, boxY, boxWidth, boxHeight);

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("FICHA DE CLIENTE", boxX + boxWidth / 2, boxY + 8, {
    align: "center",
  });

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(dayjs().format("DD/MM/YYYY"), boxX + boxWidth / 2, boxY + 16, {
    align: "center",
  });

  y += 30;

  // ==================== INFORMACIÓN GENERAL ====================
  const isActive = client.is_active ?? client.isActive;
  const estadoFlags = [
    isActive ? "Activo" : "Inactivo",
    client.risk_checked ? "Verificado" : "No verificado",
    client.siniestro ? "Siniestro" : null,
    client.cobranza_judicial ? "Cobranza judicial" : null,
  ].filter(Boolean);

  y = sectionHeader(y, "INFORMACIÓN GENERAL");
  const infoBoxHeight = 24;
  doc.setLineWidth(0.3);
  doc.rect(margin, y, pageWidth - 2 * margin, infoBoxHeight);

  const dividerX = pageWidth / 2 + 15;
  doc.line(dividerX, y, dividerX, y + infoBoxHeight);

  const leftLabelX = margin + 3;
  const leftValueX = margin + 32;
  const rightLabelX = dividerX + 3;
  const rightValueX = dividerX + 32;

  const leftValueRight = dividerX - 2;
  let rowY = y + 6;
  field(
    leftLabelX,
    leftValueX,
    rowY,
    "RAZÓN SOCIAL:",
    client.name || client.company,
    leftValueRight
  );
  field(rightLabelX, rightValueX, rowY, "VENDEDOR:", client.sellerName);

  rowY += 5;
  const rut = formatRut(client.taxId || client.tax_id);
  field(leftLabelX, leftValueX, rowY, "R.U.T.:", rut, leftValueRight);
  field(rightLabelX, rightValueX, rowY, "CANAL:", c.sales_channel_name);

  rowY += 5;
  field(leftLabelX, leftValueX, rowY, "GIRO:", client.activity, leftValueRight);
  field(rightLabelX, rightValueX, rowY, "ZONA:", client.zone);

  rowY += 5;
  field(
    leftLabelX,
    leftValueX,
    rowY,
    "ESTADO:",
    estadoFlags.join(" · "),
    leftValueRight
  );
  field(rightLabelX, rightValueX, rowY, "TIPO AUX.:", c.auxiliary_type_name);

  y += infoBoxHeight + 2;

  // ==================== CONDICIONES COMERCIALES ====================
  y = sectionHeader(y, "CONDICIONES COMERCIALES");
  const condBoxHeight = 16;
  doc.rect(margin, y, pageWidth - 2 * margin, condBoxHeight);

  const cellWidth = (pageWidth - 2 * margin) / 3;
  const cellX = [margin, margin + cellWidth, margin + 2 * cellWidth];
  doc.line(cellX[1], y, cellX[1], y + condBoxHeight);
  doc.line(cellX[2], y, cellX[2], y + condBoxHeight);

  const cellField = (x: number, label: string, value: unknown) => {
    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    doc.text(label, x + 2, y + 5);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(fitText(value, cellWidth - 4), x + 2, y + 12);
  };

  cellField(cellX[0], "CONDICIÓN DE VENTA", salesConditionName);
  cellField(
    cellX[1],
    "LÍNEA DE CRÉDITO",
    client.insuredAmount ? `$${formatCurrency(client.insuredAmount)}` : DASH
  );
  cellField(
    cellX[2],
    "DÍAS PROMEDIO DE PAGO",
    client.trailingPaymentDays ? `${client.trailingPaymentDays} días` : DASH
  );

  y += condBoxHeight + 2;

  // ==================== CONTACTOS (two side-by-side boxes) ====================
  const gap = 4;
  const halfWidth = (pageWidth - 2 * margin - gap) / 2;
  const contactLeftX = margin;
  const contactRightX = margin + halfWidth + gap;
  const contactBoxHeight = 20;

  const contactBand = (x: number, title: string) => {
    doc.setFillColor(240, 240, 240);
    doc.rect(x, y, halfWidth, 6, "F");
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(x, y, halfWidth, 6);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(title, x + 2, y + 4);
  };

  contactBand(contactLeftX, "CONTACTO COMERCIAL");
  contactBand(contactRightX, "CONTACTO DE COBRANZA");

  const contactBodyY = y + 6;
  doc.rect(contactLeftX, contactBodyY, halfWidth, contactBoxHeight);
  doc.rect(contactRightX, contactBodyY, halfWidth, contactBoxHeight);

  const contactRows = (x: number, person: unknown, email: unknown, phone: unknown) => {
    const labelX = x + 2;
    const valueX = x + 24;
    const valueRight = x + halfWidth - 2;
    field(labelX, valueX, contactBodyY + 6, "NOMBRE:", person, valueRight);
    field(labelX, valueX, contactBodyY + 12, "EMAIL:", email, valueRight);
    field(labelX, valueX, contactBodyY + 18, "TELÉFONO:", phone, valueRight);
  };

  contactRows(contactLeftX, c.contact_person, client.email, client.phone);
  contactRows(
    contactRightX,
    c.collection_contact_person,
    c.collection_contact_email,
    c.collection_contact_phone
  );

  y = contactBodyY + contactBoxHeight + 2;

  // ==================== DIRECCIÓN FISCAL ====================
  y = sectionHeader(y, "DIRECCIÓN FISCAL");
  const fiscalBoxHeight = 10;
  doc.rect(margin, y, pageWidth - 2 * margin, fiscalBoxHeight);
  field(
    margin + 3,
    margin + 28,
    y + 6,
    "DIRECCIÓN:",
    formatAddressLine(fiscalAddress)
  );
  y += fiscalBoxHeight + 2;

  // ==================== DIRECCIONES DE DESPACHO (table) ====================
  const colWidths = [10, 70, 34, 30, 30, 16];
  const colHeaders = ["#", "DIRECCIÓN", "COMUNA", "CIUDAD", "REGIÓN", "PRINCIPAL"];
  const colX: number[] = [margin];
  for (let i = 1; i < colWidths.length; i++) {
    colX.push(colX[i - 1] + colWidths[i - 1]);
  }
  const rowHeight = 6;

  const drawTableHeader = (headerY: number): number => {
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, headerY, pageWidth - 2 * margin, 6, "F");
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, headerY, pageWidth - 2 * margin, 6);
    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    colHeaders.forEach((header, i) => {
      doc.text(header, colX[i] + 1, headerY + 4);
    });
    colX.forEach((x) => doc.line(x, headerY, x, headerY + 6));
    doc.line(rightEdge, headerY, rightEdge, headerY + 6);
    return headerY + 6;
  };

  const drawTableTitle = (titleY: number): number =>
    sectionHeader(titleY, "DIRECCIONES DE DESPACHO");

  // Compact continuation header for overflow pages.
  const drawContinuationHeader = (): number => {
    let hy = 14;
    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("ALIMENTOS Y ACEITES SPA", margin, hy);
    doc.setFontSize(9);
    doc.text(
      `FICHA — ${client.name || client.company || ""}`,
      rightEdge,
      hy,
      { align: "right" }
    );
    hy += 2;
    doc.setLineWidth(0.3);
    doc.line(margin, hy, rightEdge, hy);
    return hy + 4;
  };

  y = drawTableTitle(y);

  if (addresses.length === 0) {
    const emptyHeight = 8;
    doc.rect(margin, y, pageWidth - 2 * margin, emptyHeight);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Sin direcciones de despacho registradas",
      margin + 3,
      y + 5
    );
    y += emptyHeight;
  } else {
    let bodyTop = drawTableHeader(y);
    let rowTop = bodyTop;
    const footerReserve = 14;

    addresses.forEach((address, index) => {
      // Break to a new page when the next row would collide with the footer.
      if (rowTop + rowHeight > pageHeight - footerReserve) {
        // Close the current segment box.
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.rect(margin, bodyTop, pageWidth - 2 * margin, rowTop - bodyTop);
        colX.forEach((x) => doc.line(x, bodyTop, x, rowTop));
        doc.line(rightEdge, bodyTop, rightEdge, rowTop);

        doc.addPage();
        const continued = drawTableTitle(drawContinuationHeader());
        bodyTop = drawTableHeader(continued);
        rowTop = bodyTop;
      }

      if (index > 0) {
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, rowTop, rightEdge, rowTop);
        doc.setDrawColor(0);
      }

      const streetLine =
        [address.street, address.number].filter(Boolean).join(" ").trim() +
        (address.complement ? `, ${address.complement}` : "");

      doc.setFontSize(6);
      doc.setFont("helvetica", "normal");
      const baseY = rowTop + 4;
      doc.text(String(index + 1), colX[0] + 1, baseY);
      doc.text(fitText(streetLine, colWidths[1] - 2), colX[1] + 1, baseY);
      doc.text(fitText(address.comuna, colWidths[2] - 2), colX[2] + 1, baseY);
      doc.text(fitText(address.city, colWidths[3] - 2), colX[3] + 1, baseY);
      doc.text(fitText(address.state, colWidths[4] - 2), colX[4] + 1, baseY);
      doc.text(address.isDefault ? "Sí" : "No", colX[5] + 1, baseY);

      rowTop += rowHeight;
    });

    // Close the final segment box.
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, bodyTop, pageWidth - 2 * margin, rowTop - bodyTop);
    colX.forEach((x) => doc.line(x, bodyTop, x, rowTop));
    doc.line(rightEdge, bodyTop, rightEdge, rowTop);
    y = rowTop;
  }

  // ==================== FOOTER ====================
  const pageCount = doc.getNumberOfPages();
  const generatedAt = dayjs().format("DD/MM/YYYY HH:mm");
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);
    const footerText =
      pageCount > 1
        ? `Documento generado el ${generatedAt}  ·  Página ${p} de ${pageCount}`
        : `Documento generado el ${generatedAt}`;
    doc.text(footerText, pageWidth / 2, pageHeight - 5, { align: "center" });
  }
  doc.setTextColor(0);

  const safeName =
    (client.name || client.company || "cliente")
      .replace(/[^\p{L}\p{N}]+/gu, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase() || "cliente";
  const filename = `ficha_cliente_${safeName}_${dayjs().format("YYYY-MM-DD")}.pdf`;
  doc.save(filename);

  return filename;
}
